import React, {
  useState,
  useRef,
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../lib/theme';

export interface MfaCodeInputProps {
  onComplete: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
  /** Reset the input (clear all digits) when this key changes */
  resetKey?: number;
}

/**
 * 6-digit OTP code input.
 *
 * - One box per digit (48 × 56 px, monospace, large font)
 * - Auto-advances focus on each keystroke
 * - Handles backspace: moves focus back and clears prior box
 * - Paste: distributes up to 6 digits across boxes, auto-submits on 6
 * - Non-numeric input is silently ignored
 * - Auto-submits via onComplete when all 6 digits are filled
 * - aria-group + per-digit labels for screen-reader friendliness
 */
export function MfaCodeInput({
  onComplete,
  disabled = false,
  error = false,
  resetKey,
}: MfaCodeInputProps) {
  const { t: tr } = useTranslation();
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  // Clear all digits whenever resetKey changes (e.g. after a failed verify)
  useEffect(() => {
    if (resetKey !== undefined) {
      setDigits(Array(6).fill(''));
      refs.current[0]?.focus();
    }
  }, [resetKey]);

  const handleChange = (idx: number, value: string) => {
    // Only allow a single digit; strip non-numeric
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = digit;
    setDigits(next);

    if (digit && idx < 5) {
      refs.current[idx + 1]?.focus();
    }

    if (next.every(d => d !== '')) {
      onComplete(next.join(''));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;

    const next = [...Array(6).fill('')];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setDigits(next);

    if (pasted.length >= 6) {
      onComplete(pasted.slice(0, 6));
      refs.current[5]?.focus();
    } else {
      refs.current[pasted.length]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[idx]) {
        // Clear current box
        const next = [...digits];
        next[idx] = '';
        setDigits(next);
      } else if (idx > 0) {
        // Clear previous box and move focus back
        const next = [...digits];
        next[idx - 1] = '';
        setDigits(next);
        refs.current[idx - 1]?.focus();
      }
      e.preventDefault();
    }

    if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && idx < 5) {
      refs.current[idx + 1]?.focus();
    }
  };

  const boxBase: React.CSSProperties = {
    width: 48,
    height: 56,
    textAlign: 'center',
    fontSize: 24,
    fontWeight: 600,
    fontFamily: 'monospace',
    borderRadius: 8,
    border: `2px solid ${error ? '#f04438' : '#d0d5dd'}`,
    outline: 'none',
    background: '#fff',
    color: '#101828',
    cursor: disabled ? 'not-allowed' : 'text',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };

  return (
    <div
      role="group"
      aria-label={tr('mfa.manager.code_aria')}
      style={{ display: 'flex', gap: 10, justifyContent: 'center' }}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={d}
          autoFocus={i === 0}
          disabled={disabled}
          aria-label={tr('mfa.code_input.digit_aria', { index: i + 1 })}
          aria-invalid={error}
          aria-required="true"
          onChange={e => handleChange(i, e.target.value)}
          onPaste={handlePaste}
          onKeyDown={e => handleKeyDown(i, e)}
          onFocus={e => e.target.select()}
          style={{
            ...boxBase,
            borderColor: error
              ? '#f04438'
              : d
              ? BRAND
              : '#d0d5dd',
            boxShadow: error
              ? '0 0 0 3px rgba(240,68,56,0.15)'
              : undefined,
          }}
        />
      ))}
    </div>
  );
}
